import {
  DentureItemOrderTypeEnum,
  type DentureItemOrderType,
} from "@jasper/client-models";
import { ItemType, WorkflowType } from "../types/enums";
import type {
  PartialDenture,
  PartialDentureArcadeData,
  Product,
  RemovableItem,
} from "../types/interfaces";
import { hasMaxillaryTeeth } from "./item.utils";

// Values of a PAP / PAC description, shared by ItemsDescription and the order
// detail v2.

type Labels = { en: string; fr: string };

const translate = (
  labels: Labels | undefined,
  shouldTranslate: boolean
): string | undefined => labels?.[shouldTranslate ? "en" : "fr"];

const SHADE_TYPE_LABELS: Partial<Record<string, Labels>> = {
  VITA_CLASSIC: { en: "Vita Classical", fr: "Vita Classical" },
  VITA_3D_MASTER: { en: "Vita 3D Master", fr: "Vita 3D Master" },
  CHROMASCOP: { en: "Chromascop", fr: "Chromascop" },
  SR_VIVODENT_SPE: { en: "Sr Vivodent Spe", fr: "Sr Vivodent Spe" },
};

const TEETH_SHAPE_LABELS: Partial<Record<string, Labels>> = {
  SQUARE: { en: "Square", fr: "Carrées" },
  ROUNDED: { en: "Rounded", fr: "Arrondies" },
  TRIANGULAR: { en: "Triangular", fr: "Triangulaires" },
};

const MATERIAL_ID_LABELS: Partial<Record<string, Labels>> = {
  ACRYLIC: { en: "Acrylic", fr: "Résine" },
  COBALT_CHROME_METALLIC_FRAME: {
    en: "Cobalt Chrome Metallic Frame",
    fr: "Chassis métallique Cobalt-Chrome",
  },
  TITANIUM_FRAME: { en: "Titanium Frame", fr: "Chassis Titane" },
  VALPLAST: { en: "Valplast", fr: "Valplast" },
};

const GINGIVA_ID_LABELS: Partial<Record<string, Labels>> = {
  GINGIVA_RED_PINK: { en: "Gingiva Red Pink", fr: "Rose rouge" },
  GINGIVA_LIGHT_PINK: { en: "Gingiva light pink", fr: "Rose clair" },
  GINGIVA_STANDARD: { en: "Gingiva standard", fr: "Classique" },
  GINGIVA_DARK_PINK: { en: "Gingiva dark pink", fr: "Rose foncé" },
};

const WORKFLOW_TYPE_LABELS: Record<WorkflowType, Labels> = {
  [WorkflowType.WAX_RIM]: { en: "Wax rim", fr: "Cire d'occlusion" },
  [WorkflowType.ONE_APPOINTMENT_DIRECT]: {
    en: "Direct finish",
    fr: "Finition directe",
  },
  [WorkflowType.WITH_FITTING]: { en: "Set up try in", fr: "Essayage" },
};

const FINISH_LABELS: Labels = { en: "Finish", fr: "Finition" };

const FULL_DENTURE_ORDER_TYPE_NAMES: Partial<Record<string, Labels>> = {
  WAX_BITE: { en: "Wax Rim", fr: "Cire d'occlusion" },
  TRY_IN: { en: "Set Up Try In", fr: "Essayage" },
  FINAL: { en: "Direct Finish", fr: "Prothèse amovible complète finale" },
};

const IMPRINT_TYPE_LABELS: Partial<Record<string, Labels>> = {
  EDENTULOUS_ARCH_RDV1: {
    en: "Edentulous arch - Appointment 1",
    fr: "Arcade(s) édentée(s) - RDV 1",
  },
  PRE_EXISTING_DENTURE_RDV1: {
    en: "Pre-existing denture - Appointment 1",
    fr: "Prothèse(s) pré-existante(s) du patient - RDV 1",
  },
  WAX_BITE_RDV2: {
    en: "Wax Rim - Appointment 2",
    fr: "Cire d'occlusion - RDV 2",
  },
  TRY_IN_RDV2: {
    en: "Set Up Try In - Appointment 2",
    fr: "Essayage - RDV 2",
  },
};

const PARTIAL_DENTURE_OPTIONS: {
  key: keyof Pick<
    PartialDenture,
    "lingualMetalBand" | "metalMesh" | "palatalMetalBand" | "customTray"
  >;
  labels: Labels;
}[] = [
  {
    key: "lingualMetalBand",
    labels: { en: "Lingual metal band", fr: "Bande métallique lingual" },
  },
  { key: "metalMesh", labels: { en: "Metal mesh", fr: "Maille métallique" } },
  {
    key: "palatalMetalBand",
    labels: { en: "Palatal metal band", fr: "Bande métallique palatine" },
  },
  {
    key: "customTray",
    labels: { en: "Custom tray", fr: "Porte-empreinte individuel" },
  },
];

type ShadeChoices = {
  getItemShadesWhere?: { id: string; label?: string | null }[];
};

// Undefined for a shade without guide (gingiva) or an unknown one, so callers choose what to write instead
export const findShadeTypeLabel = (
  shadeType: string,
  shouldTranslate = false
): string | undefined =>
  translate(SHADE_TYPE_LABELS[shadeType], shouldTranslate);

export const getDentureShadeTypeLabel = (
  shadeType: string,
  shouldTranslate = false
): string => findShadeTypeLabel(shadeType, shouldTranslate) ?? shadeType;

export const getTeethShapeLabel = (
  teethShape: string,
  shouldTranslate = false
): string =>
  translate(TEETH_SHAPE_LABELS[teethShape], shouldTranslate) ?? teethShape;

export const getDentureMaterialLabel = (
  materialId: string,
  shouldTranslate = false
): string =>
  translate(MATERIAL_ID_LABELS[materialId], shouldTranslate) ?? materialId;

export const getGingivaShadeLabel = (
  gingivaId: string,
  shouldTranslate = false
): string =>
  translate(GINGIVA_ID_LABELS[gingivaId], shouldTranslate) ?? gingivaId;

export const getDentureShadeLabel = (
  shadeId: string,
  shouldTranslate = false,
  choicesData?: ShadeChoices
): string => {
  const shade = choicesData?.getItemShadesWhere?.find(
    choice => choice.id === shadeId
  );
  if (shade?.label) {
    return shade.label;
  }

  for (const shadeType of Object.keys(SHADE_TYPE_LABELS)) {
    if (shadeId.includes(shadeType)) {
      const shadeValue = shadeId.split(`_${shadeType}`)[0];
      return `${shadeValue} (${getDentureShadeTypeLabel(shadeType, shouldTranslate)})`;
    }
  }

  if (shadeId.includes("VITA_MASTER")) {
    const shadeValue = shadeId.split("_VITA_MASTER")[0];
    return `${shadeValue} (${getDentureShadeTypeLabel("VITA_3D_MASTER", shouldTranslate)})`;
  }
  return shadeId.replace(/_/g, " ");
};

export const getPartialDentureArchPreferences = (
  item: RemovableItem,
  product?: Product
): PartialDentureArcadeData => {
  const preferences = product?.itemPreferences;
  const arcadeData = preferences?.partialDentureArcadeData;

  if (!arcadeData) {
    return {
      shadeType: preferences?.shadeType,
      teethShape: preferences?.teethShape,
    };
  }
  return (
    (hasMaxillaryTeeth(item.teeth ?? [])
      ? arcadeData.maxillaire
      : arcadeData.mandibulaire) ?? {}
  );
};

export const getTeethToRemove = (item: RemovableItem): number[] =>
  Array.isArray(item.teeth)
    ? item.teeth.filter(
        tooth => !item.partialDenture?.missingTeeth?.includes(tooth)
      )
    : [];

export const getPartialDentureOptionLabels = (
  item: RemovableItem,
  shouldTranslate = false
): string[] =>
  PARTIAL_DENTURE_OPTIONS.filter(({ key }) => item.partialDenture?.[key]).map(
    ({ labels }) => translate(labels, shouldTranslate) ?? ""
  );

export const isDirectFinish = (item: RemovableItem): boolean =>
  (item.rdvNumber === 1 ||
    item.rdvNumber === null ||
    item.rdvNumber === undefined) &&
  (item.workflowType === WorkflowType.ONE_APPOINTMENT_DIRECT ||
    item.partialDentureOrderType === DentureItemOrderTypeEnum.FINAL ||
    item.fullDentureOrderType === DentureItemOrderTypeEnum.FINAL) &&
  (item.itemType === ItemType.PARTIAL_DENTURE ||
    item.itemType === ItemType.FULL_DENTURE);

export const getWorkflowTypeLabel = (
  workflowType: WorkflowType | undefined,
  shouldTranslate = false
): string =>
  (workflowType &&
    translate(WORKFLOW_TYPE_LABELS[workflowType], shouldTranslate)) ||
  "";

export const getDentureOrderTypeLabel = (
  orderType: DentureItemOrderType | undefined,
  item: RemovableItem,
  shouldTranslate = false
): string => {
  switch (orderType) {
    case DentureItemOrderTypeEnum.WAX_BITE:
      return getWorkflowTypeLabel(WorkflowType.WAX_RIM, shouldTranslate);
    case DentureItemOrderTypeEnum.TRY_IN:
      return getWorkflowTypeLabel(WorkflowType.WITH_FITTING, shouldTranslate);
    case DentureItemOrderTypeEnum.FINAL:
      return isDirectFinish(item)
        ? getWorkflowTypeLabel(
            WorkflowType.ONE_APPOINTMENT_DIRECT,
            shouldTranslate
          )
        : (translate(FINISH_LABELS, shouldTranslate) ?? "");
    default:
      return "";
  }
};

export const getFullDentureOrderTypeName = (
  item: RemovableItem,
  shouldTranslate = false
): string | undefined =>
  translate(
    FULL_DENTURE_ORDER_TYPE_NAMES[item.fullDentureOrderType ?? ""],
    shouldTranslate
  ) ?? item.fullDentureOrderType;

export const getImprintTypeLabel = (
  item: RemovableItem,
  shouldTranslate = false
): string | undefined =>
  translate(IMPRINT_TYPE_LABELS[item.imprintType ?? ""], shouldTranslate) ??
  item.imprintType;
